import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { BarChart2, Loader2, Users, Building2 } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";

// Import the existing visualization components
import AcceptanceDistribution from "./visualizations/AcceptanceDistribution";
import SelfAssessmentChange from "./visualizations/SelfAssessmentChange";
import SelfSelectionDistribution from "./visualizations/SelfSelectionDistribution";

const SimpleGroupComparison = ({ situationId, supabase }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState({
    value1: "",
    value2: "",
  });
  const [compareMode, setCompareMode] = useState("category"); // "category" or "facility"
  const [selectedFacility1, setSelectedFacility1] = useState("");
  const [selectedFacility2, setSelectedFacility2] = useState("");
  const [compareData, setCompareData] = useState({
    group1: null,
    group2: null,
  });
  const [user] = useState(JSON.parse(localStorage.getItem("user")));
  const [isComparing, setIsComparing] = useState(false);
  const [selectedVisualization, setSelectedVisualization] =
    useState("selfAssessment");

  // Define visualization options
  const visualizations = {
    acceptance: {
      component: AcceptanceDistribution,
      title: "Distribúcia akceptácie",
    },
    selfAssessment: {
      component: SelfAssessmentChange,
      title: "Distribúcia hodnotení",
    },
    selfSelection: {
      component: SelfSelectionDistribution,
      title: "Sebahodnotenie",
    },
  };

  useEffect(() => {
    console.log(
      "SimpleGroupComparison initialized with situationId:",
      situationId
    );

    Promise.all([fetchCategories(), fetchFacilities()])
      .catch((err) => {
        console.error("Failed to fetch initial data:", err);
        setError("Nepodarilo sa načítať údaje");
      })
      .finally(() => setLoading(false));
  }, []);

  // Update category options when category changes
  useEffect(() => {
    if (selectedCategory) {
      const category = categories.find((c) => c.id === selectedCategory);
      if (category) {
        console.log(
          "Category options updated:",
          category.custom_category_options
        );
        setCategoryOptions(category.custom_category_options || []);
        setSelectedValues({ value1: "", value2: "" });
      }
    }
  }, [selectedCategory, categories]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("custom_categories")
        .select(
          `
          id,
          name,
          custom_category_options (
            id,
            value
          )
        `
        )
        .eq("company_id", user.company_id)
        .eq("creator_id", user.id)
        .eq("is_active", true);

      if (error) throw error;
      console.log("Fetched categories:", data);
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories");
      return Promise.reject(error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const { data, error } = await supabase
        .from("facilities")
        .select("id, name")
        .eq("company_id", user.company_id)
        .eq("is_active", true);

      if (error) throw error;
      console.log("Fetched facilities:", data);
      setFacilities(data || []);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setError("Failed to load facilities");
      return Promise.reject(error);
    }
  };

  const checkEvaluationsExist = async (userIds) => {
    if (!userIds.length) return false;

    try {
      // First, get all solutions for this situation
      const { data: solutions } = await supabase
        .from("solutions")
        .select("id")
        .eq("situation_id", situationId);

      if (!solutions || solutions.length === 0) {
        console.log("No solutions found for situation:", situationId);
        return false;
      }

      // Check if there are evaluations from these users
      const { data: evaluations, error } = await supabase
        .from("evaluations")
        .select("id")
        .in(
          "solution_id",
          solutions.map((s) => s.id)
        )
        .in("evaluator_id", userIds)
        .limit(1);

      if (error) throw error;

      const hasEvaluations = evaluations && evaluations.length > 0;
      console.log(
        `${hasEvaluations ? "Found" : "No"} evaluations for users:`,
        userIds
      );
      return hasEvaluations;
    } catch (error) {
      console.error("Error checking evaluations:", error);
      return false;
    }
  };

  const handleCompare = async () => {
    if (compareMode === "category") {
      if (
        !selectedCategory ||
        !selectedValues.value1 ||
        !selectedValues.value2
      ) {
        setError("Vyberte kategóriu a dve hodnoty na porovnanie");
        return;
      }
    } else {
      // facility mode
      if (!selectedFacility1 || !selectedFacility2) {
        setError("Vyberte dve prevádzky na porovnanie");
        return;
      }
    }

    setLoading(true);
    setError(null);
    setDebugInfo(null);

    try {
      let group1Users = [],
        group2Users = [];
      let group1Name, group2Name;

      if (compareMode === "category") {
        // Get users for each selected category option
        group1Users = await fetchUsersForOption(
          selectedCategory,
          selectedValues.value1
        );
        group2Users = await fetchUsersForOption(
          selectedCategory,
          selectedValues.value2
        );
        group1Name =
          categoryOptions.find((o) => o.id === selectedValues.value1)?.value ||
          "Skupina 1";
        group2Name =
          categoryOptions.find((o) => o.id === selectedValues.value2)?.value ||
          "Skupina 2";
      } else {
        // Get users for each selected facility
        group1Users = await fetchUsersForFacility(selectedFacility1);
        group2Users = await fetchUsersForFacility(selectedFacility2);
        group1Name =
          facilities.find((f) => f.id === selectedFacility1)?.name ||
          "Prevádzka 1";
        group2Name =
          facilities.find((f) => f.id === selectedFacility2)?.name ||
          "Prevádzka 2";
      }

      // Debug information
      const debugData = {
        situationId,
        group1: {
          name: group1Name,
          userCount: group1Users.length,
          users: group1Users,
        },
        group2: {
          name: group2Name,
          userCount: group2Users.length,
          users: group2Users,
        },
      };

      console.log("Comparison data:", debugData);
      setDebugInfo(debugData);

      // Check if at least one group has users
      if (group1Users.length === 0 && group2Users.length === 0) {
        setError("Neboli nájdení žiadni používatelia pre porovnanie");
        setLoading(false);
        return;
      }

      // Fetch all solutions with evaluations for this situation
      const { data: allSolutions, error: solutionsError } = await supabase
        .from("solutions")
        .select(
          `
          id,
          user_id,
          content,
          users (
            id,
            first_name,
            last_name
          ),
          evaluations (
            id, 
            evaluator_id, 
            score,
            users (
              id,
              first_name,
              last_name
            )
          )
        `
        )
        .eq("situation_id", situationId);

      if (solutionsError) throw solutionsError;

      // Filter solutions and evaluations for each group
      const group1Solutions = allSolutions.filter((solution) => {
        // Only include solutions authored by users in group1
        return group1Users.includes(solution.user_id);
      });

      const group2Solutions = allSolutions.filter((solution) => {
        // Only include solutions authored by users in group2
        return group2Users.includes(solution.user_id);
      });

      // Check if any group has relevant solutions/evaluations
      const group1HasData = group1Solutions.length > 0;
      const group2HasData = group2Solutions.length > 0;

      // Store the filtered user IDs and solutions for the visualization components
      setCompareData({
        group1: {
          userIds: group1Users,
          name: group1Name,
          hasEvaluations: group1HasData,
          solutions: group1Solutions,
        },
        group2: {
          userIds: group2Users,
          name: group2Name,
          hasEvaluations: group2HasData,
          solutions: group2Solutions,
        },
      });

      setIsComparing(true);
    } catch (error) {
      console.error("Error comparing groups:", error);
      setError("Nepodarilo sa porovnať skupiny: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersForOption = async (categoryId, optionId) => {
    try {
      const { data, error } = await supabase
        .from("user_category_values")
        .select("user_id")
        .eq("category_id", categoryId)
        .eq("option_id", optionId);

      if (error) throw error;
      console.log(
        `Fetched ${data.length} users for category ${categoryId}, option ${optionId}`
      );
      return data.map((item) => item.user_id);
    } catch (error) {
      console.error("Error fetching users for option:", error);
      return [];
    }
  };

  const fetchUsersForFacility = async (facilityId) => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id")
        .eq("facility_id", facilityId)
        .eq("is_active", true);

      if (error) throw error;
      console.log(`Fetched ${data.length} users for facility ${facilityId}`);
      return data.map((user) => user.id);
    } catch (error) {
      console.error("Error fetching users for facility:", error);
      return [];
    }
  };

  const renderComparisonResults = () => {
    if (!isComparing || !compareData.group1 || !compareData.group2) return null;

    // Get the current visualization component
    const VisualizationComponent =
      visualizations[selectedVisualization].component;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center border-b pb-4">
          <h3 className="text-lg font-medium">
            Porovnanie skupín: {visualizations[selectedVisualization].title}
          </h3>

          <Select
            value={selectedVisualization}
            onValueChange={setSelectedVisualization}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Typ vizualizácie" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(visualizations).map(([key, { title }]) => (
                <SelectItem key={key} value={key}>
                  {title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First group visualization */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">{compareData.group1.name}</h4>
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {compareData.group1.userIds.length} používateľov
              </Badge>
            </div>
            <div className="border rounded-lg p-4 min-h-[300px] flex justify-center items-center">
              {compareData.group1.userIds.length === 0 ? (
                <div className="text-gray-500 text-center">
                  Žiadni používatelia v tejto skupine
                </div>
              ) : !compareData.group1.hasEvaluations ? (
                <div className="text-gray-500 text-center">
                  Žiadne dáta o hodnoteniach nie sú dostupné
                </div>
              ) : (
                <div className="w-full h-full">
                  <VisualizationComponent
                    key={`${selectedVisualization}-group1-${situationId}`}
                    situationId={situationId}
                    supabase={supabase}
                    filteredUserIds={compareData.group1.userIds}
                    evaluationData={{ solutions: compareData.group1.solutions }}
                    hideHeader={true}
                    className="h-[300px] w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Second group visualization */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">{compareData.group2.name}</h4>
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {compareData.group2.userIds.length} používateľov
              </Badge>
            </div>
            <div className="border rounded-lg p-4 min-h-[300px] flex justify-center items-center">
              {compareData.group2.userIds.length === 0 ? (
                <div className="text-gray-500 text-center">
                  Žiadni používatelia v tejto skupine
                </div>
              ) : !compareData.group2.hasEvaluations ? (
                <div className="text-gray-500 text-center">
                  Žiadne dáta o hodnoteniach nie sú dostupné
                </div>
              ) : (
                <div className="w-full h-full">
                  <VisualizationComponent
                    key={`${selectedVisualization}-group2-${situationId}`}
                    situationId={situationId}
                    supabase={supabase}
                    filteredUserIds={compareData.group2.userIds}
                    evaluationData={{ solutions: compareData.group2.solutions }}
                    hideHeader={true}
                    className="h-[300px] w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <BarChart2 className="w-5 h-5" />
          <CardTitle>Porovnanie skupín</CardTitle>
        </div>
        <CardDescription>
          Porovnanie distribúcie hodnotení medzi skupinami
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            {!isComparing ? (
              <div className="space-y-6">
                <div className="flex space-x-4 border-b pb-4">
                  <Button
                    variant={compareMode === "category" ? "default" : "outline"}
                    onClick={() => setCompareMode("category")}
                    className="flex items-center"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Podľa kategórie
                  </Button>
                  <Button
                    variant={compareMode === "facility" ? "default" : "outline"}
                    onClick={() => setCompareMode("facility")}
                    className="flex items-center"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Podľa prevádzky
                  </Button>
                </div>

                <div>
                  <Label>Typ vizualizácie</Label>
                  <Select
                    value={selectedVisualization}
                    onValueChange={setSelectedVisualization}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Vyberte typ" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(visualizations).map(
                        ([key, { title }]) => (
                          <SelectItem key={key} value={key}>
                            {title}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {compareMode === "category" ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Kategória</Label>
                      <Select
                        value={selectedCategory}
                        onValueChange={setSelectedCategory}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Vyberte kategóriu" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Prvá hodnota</Label>
                        <Select
                          value={selectedValues.value1}
                          onValueChange={(value) =>
                            setSelectedValues((prev) => ({
                              ...prev,
                              value1: value,
                            }))
                          }
                          disabled={!selectedCategory}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte prvú hodnotu" />
                          </SelectTrigger>
                          <SelectContent>
                            {categoryOptions.map((option) => (
                              <SelectItem
                                key={option.id}
                                value={option.id}
                                disabled={option.id === selectedValues.value2}
                              >
                                {option.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Druhá hodnota</Label>
                        <Select
                          value={selectedValues.value2}
                          onValueChange={(value) =>
                            setSelectedValues((prev) => ({
                              ...prev,
                              value2: value,
                            }))
                          }
                          disabled={!selectedCategory}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte druhú hodnotu" />
                          </SelectTrigger>
                          <SelectContent>
                            {categoryOptions.map((option) => (
                              <SelectItem
                                key={option.id}
                                value={option.id}
                                disabled={option.id === selectedValues.value1}
                              >
                                {option.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Prvá prevádzka</Label>
                        <Select
                          value={selectedFacility1}
                          onValueChange={setSelectedFacility1}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte prvú prevádzku" />
                          </SelectTrigger>
                          <SelectContent>
                            {facilities.map((facility) => (
                              <SelectItem
                                key={facility.id}
                                value={facility.id}
                                disabled={facility.id === selectedFacility2}
                              >
                                {facility.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Druhá skupina</Label>
                        <Select
                          value={selectedFacility2}
                          onValueChange={setSelectedFacility2}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte druhú skupinu" />
                          </SelectTrigger>
                          <SelectContent>
                            {facilities.map((facility) => (
                              <SelectItem
                                key={facility.id}
                                value={facility.id}
                                disabled={facility.id === selectedFacility1}
                              >
                                {facility.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleCompare}
                    disabled={
                      compareMode === "category"
                        ? !selectedCategory ||
                          !selectedValues.value1 ||
                          !selectedValues.value2
                        : !selectedFacility1 || !selectedFacility2
                    }
                  >
                    <Users className="w-4 h-4 mr-2" />
                    <span>Porovnať skupiny</span>
                  </Button>
                </div>
              </div>
            ) : (
              renderComparisonResults()
            )}
          </>
        )}
      </CardContent>
      {isComparing && (
        <CardFooter>
          <Button variant="outline" onClick={() => setIsComparing(false)}>
            Nové porovnanie
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default SimpleGroupComparison;
